import {Language} from 'yougile-lang'
import {ObjUtil} from 'yougile-util'
import {DataObjectId, isDataObjectId} from './data-object'
import {BoardPermissions, PermissionsById} from './permissions'
import {UserUITheme} from './user'
import {UserAccount} from './user-account'

export const sharingGuestAccount: Readonly<UserAccount> = {
  avatar: '/img/sharing/guest.svg',
  dndUntil: 0,
  id: '00000000-7ccf-4f20-8b7b-76f505208331' as DataObjectId,
  lastActivity: Date.now(),
  realName: 'Guest',
  status: 'online'
}

export type BoardShareType =
  | 'read-only'
  | 'chat-only'
  | 'add-tasks'
  | 'add-tasks-only'

export interface ShareBoardPermissions {
  chat: boolean
  addTasks: boolean
}

export const shareTypeToPermissions = (
  type: BoardShareType
): ShareBoardPermissions => {
  return {
    addTasks: type === 'add-tasks' || type === 'add-tasks-only',
    chat: type === 'chat-only' || type === 'add-tasks'
  }
}

export const sharePermissionsToType = (
  permissions: ShareBoardPermissions
): BoardShareType => {
  const types: BoardShareType[] = [
    'read-only',
    'chat-only',
    'add-tasks-only',
    'add-tasks'
  ]
  const ix = +permissions.chat + +permissions.addTasks * 2
  return types[ix]
}

export const isBoardShareType = (s: string): s is BoardShareType => {
  return (
    (s as BoardShareType) === 'read-only' ||
    (s as BoardShareType) === 'chat-only' ||
    (s as BoardShareType) === 'add-tasks-only' ||
    (s as BoardShareType) === 'add-tasks'
  )
}

export type ShareAllowedColumns = {[columnId: string]: true} | 'all'
export const isCorrectShareAllowedColumns = (
  value: unknown
): value is ShareAllowedColumns => {
  return (
    (value as ShareAllowedColumns) === 'all' ||
    (!!value && typeof value === 'object')
  )
}

// allows to identify visibility changes for shared users
export type SharedBoardVisibility = Pick<
  SharedBoard,
  'boardId' | 'allowedColumns' | 'userId'
>

export interface SharedBoard {
  boardId: DataObjectId
  companyId: DataObjectId
  companyName?: string
  projectName?: string
  boardName?: string
  type: BoardShareType
  allowedColumns?: ShareAllowedColumns
  lang: Language
  theme: UserUITheme
  background: string
  userId: DataObjectId //special reserved user to enter shared board
  secret?: string // allows to create login keys
}

export type SharedBoardSettings = Partial<
  Pick<SharedBoard, 'background' | 'lang' | 'theme' | 'type' | 'allowedColumns'>
>

export interface SharedBoardRecords {
  [sharedId: string]: SharedBoard
}

interface AnyGuest {
  id: string
  name: string
  timestamp: number
  available: {
    [companyId: string]: {
      boards: {
        [boardId: string]: string // boardId -> sharedId
      }
      read: {
        [taskId: string]: number
      }
    }
  }
}

interface AnonymousGuest extends AnyGuest {
  key: string
}

interface RegisteredGuest extends AnyGuest {
  id: DataObjectId
  avatar: string
}

export type SharingGuest = AnonymousGuest | RegisteredGuest
export const isRegisteredGuest = (
  guest?: SharingGuest
): guest is RegisteredGuest => {
  return isDataObjectId(guest?.id)
}

export interface GuestPublicInfo {
  name: string
  avatar?: string
}

export interface BoardGuests {
  [guestId: string]: GuestPublicInfo | undefined
}

const basicPermissions = (): BoardPermissions => ({
  addColumn: false,
  delete: false,
  settings: false,
  editStickers: false,
  editTitle: false,
  showStickers: false,
  move: false,
  columns: {
    addTask: false,
    delete: false,
    editTitle: false,
    move: 'no',
    allTasks: {
      assignUsers: 'no',
      close: false,
      connect: false,
      delete: false,
      editDescription: false,
      complete: false,
      editPins: false,
      editStickers: false,
      editSubtasks: 'no',
      editTitle: false,
      editWhoToNotify: 'no',
      move: 'no',
      sendFiles: false,
      sendMessages: false,
      show: false
    }
  }
})

const readOnlyPermissions = (): BoardPermissions => {
  const basic = basicPermissions()
  basic.columns.allTasks.show = true
  return basic
}

const chatOnlyPermissions = (): BoardPermissions => {
  const ro = readOnlyPermissions()
  ro.columns.allTasks.sendMessages = true
  ro.columns.allTasks.sendFiles = true
  return ro
}

const addTaskPermissions = (): BoardPermissions => {
  const ro = readOnlyPermissions()
  ro.columns.allTasks.sendMessages = true
  ro.columns.allTasks.sendFiles = true
  ro.columns.addTask = true
  return ro
}

const addTaskOnlyPermissions = (): BoardPermissions => {
  const ro = readOnlyPermissions()
  ro.columns.addTask = true
  return ro
}

const getBoardPermissionsFromType = (
  type: BoardShareType
): BoardPermissions => {
  if (type === 'chat-only') {
    return chatOnlyPermissions()
  } else if (type === 'add-tasks') {
    return addTaskPermissions()
  } else if (type === 'add-tasks-only') {
    return addTaskOnlyPermissions()
  } else {
    return readOnlyPermissions()
  }
}

export const getSharedBoardPermissions = (
  boardId: DataObjectId,
  type: BoardShareType,
  allowedColumns: ShareAllowedColumns | undefined
): PermissionsById => {
  const permFromType = getBoardPermissionsFromType(type)
  const boardPermissions =
    allowedColumns && allowedColumns !== 'all'
      ? basicPermissions()
      : permFromType
  const res: PermissionsById = {
    [boardId]: {
      type: 'board',
      objectId: boardId,
      permissions: boardPermissions
    }
  }
  if (allowedColumns && allowedColumns !== 'all') {
    ObjUtil.forEach(allowedColumns, columnId => {
      res[columnId] = {
        type: 'column',
        objectId: columnId as DataObjectId,
        permissions: permFromType.columns
      }
    })
  }
  return res
}
